import React from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  InputNumber,
  Select,
  Typography,
  Row,
  Col,
  Divider,
  Button,
  Alert,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  EnergyProviderContractInput,
  EnergyProviderContractOutput,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import { updateContractConfigurationMutation } from "../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../util-components/Notifications/notifications";
import { biddingZones } from "../NewSite/Components/OptimizationConfigurationForm/biddingZones";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Title, Text } = Typography;

const formatDayAheadFormula = (
  coefficient?: number,
  absoluteCoefficient?: number,
  cost?: number,
) => {
  const absoluteTerm = absoluteCoefficient
    ? ` + ${absoluteCoefficient} × |Day-ahead price(t)|`
    : "";
  const sign = (cost ?? 0) >= 0 ? "+" : "-";
  return `${coefficient ?? "???"} × Day-ahead price(t)${absoluteTerm} ${sign} ${cost != null ? Math.abs(cost) : "???"}`;
};

const ABSOLUTE_COEFFICIENT_TOOLTIP =
  "Multiplies the absolute day-ahead price, for a margin that is charged on negative prices too (e.g. 0.03 for spot + 3% × |spot|).";

type ContractType = NonNullable<EnergyProviderContractOutput["contract_type"]>;

type EnergyContractFormProps = {
  site: Site;
  refetchSite: () => void;
};

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};
type UnionToIntersection<U> = Prettify<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (U extends any ? (k: U) => void : never) extends (k: infer I) => void
    ? I
    : never
>;

type FormValues = Prettify<
  Omit<
    UnionToIntersection<
      {
        [Key in ContractType]: Partial<
          Exclude<EnergyProviderContractInput, { contract_type: Key }>
        >;
      }[ContractType]
    >,
    "contract_type"
  > & { contract_type?: ContractType }
>;

export const EnergyContractForm: React.FC<EnergyContractFormProps> = ({
  site,
  refetchSite,
}) => {
  const [form] = useForm<FormValues>();
  const notify = useNotify();
  const isViewer = useIsViewer();
  const contractType = Form.useWatch("contract_type", form);
  const offtakeCoefficient = Form.useWatch(
    "offtake_day_ahead_coefficient",
    form,
  );
  const offtakeAbsoluteCoefficient = Form.useWatch(
    "offtake_absolute_day_ahead_coefficient",
    form,
  );
  const offtakeCost = Form.useWatch("offtake_cost_per_mwh", form);
  const injectionCoefficient = Form.useWatch(
    "injection_day_ahead_coefficient",
    form,
  );
  const injectionAbsoluteCoefficient = Form.useWatch(
    "injection_absolute_day_ahead_coefficient",
    form,
  );
  const injectionCost = Form.useWatch("injection_cost_per_mwh", form);

  const updateContract = useMutation({
    ...updateContractConfigurationMutation(),
  });

  const onFinish = (values: FormValues) => {
    if (!site.site_id) {
      return;
    }

    let body: Record<string, unknown> | null = values;

    if (values.contract_type == null) {
      body = null;
    }

    updateContract.mutate(
      {
        path: { site_id: site.site_id },
        // @ts-expect-error form values are not typed
        body: body ?? {},
      },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong while updating the energy contract.",
          });
        },
        onSuccess: () => {
          refetchSite();
          notify({
            type: "success",
            message: "Energy contract updated successfully!",
          });
        },
      },
    );
  };

  return (
    <div className="mt-8">
      <Title level={3}>Energy Contract</Title>
      <Typography.Paragraph type="secondary" className="mb-4">
        Adding the energy contract details allows us to provide accurate benefit
        reports and optimize charging schedules use the exact energy costs. If
        your contract can't be represented by the available options, please
        contact <a href="mailto:support@pleevi.ai">support@pleevi.ai</a>.
      </Typography.Paragraph>
      <Form
        form={form}
        initialValues={site.configurations?.contract ?? { contract_type: null }}
        onFinish={onFinish}
        layout="vertical"
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
        disabled={isViewer}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="contract_type"
              label="Contract Type"
              rules={[
                { required: true, message: "Please select a contract type" },
              ]}
            >
              <Select>
                <Select.Option value="fixed">Fixed Price</Select.Option>
                <Select.Option value="dynamic">
                  Dynamic Prices (using Day-Ahead prices)
                </Select.Option>
                <Select.Option value="day_night_be">
                  Day/Night (Belgium)
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {contractType === "fixed" && (
          <>
            <Divider orientation="left">Fixed Price Contract</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="fixed_offtake_cost_per_mwh"
                  label="Fixed Offtake Cost"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the fixed offtake price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Offtake Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="fixed_injection_cost_per_mwh"
                  label="Fixed Injection Revenue"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the fixed injection price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Injection Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {contractType === "dynamic" && (
          <>
            <Divider orientation="left">Dynamic Contract</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="day_ahead_bidding_zone"
                  label="Day-Ahead Bidding Zone"
                  rules={[
                    {
                      required: true,
                      message: "Please select a bidding zone",
                    },
                  ]}
                >
                  <Select
                    placeholder="Bidding Zone"
                    showSearch
                    options={biddingZones.map(([name, code]) => ({
                      label: `${name} (${code})`,
                      value: code,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label
                        ?.toLowerCase()
                        .includes(input.toLowerCase()) ||
                        option?.value
                          ?.toLowerCase()
                          .includes(input.toLowerCase())) ??
                      false
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Text strong>Offtake Cost</Text>
            <Row gutter={16} className="mt-2">
              <Col span={8}>
                <Form.Item
                  name="offtake_day_ahead_coefficient"
                  label="Day-Ahead Coefficient"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the day-ahead coefficient",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Coefficient"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="offtake_absolute_day_ahead_coefficient"
                  label="Absolute Day-Ahead Coefficient"
                  tooltip={ABSOLUTE_COEFFICIENT_TOOLTIP}
                  initialValue={0}
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter the absolute day-ahead coefficient",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Coefficient"
                    addonAfter="× |Day-ahead price|"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="offtake_cost_per_mwh"
                  label="Additional Cost"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the additional cost",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Additional Cost"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>
            <div className="mb-4 rounded bg-gray-200 p-3">
              <Text type="secondary">Price Formula (Offtake cost)</Text>
              <div className="mt-1 font-mono text-gray-800">
                {formatDayAheadFormula(
                  offtakeCoefficient,
                  offtakeAbsoluteCoefficient,
                  offtakeCost,
                )}
              </div>
            </div>
            <Text strong>Injection Revenue</Text>
            <Row gutter={16} className="mt-2">
              <Col span={8}>
                <Form.Item
                  name="injection_day_ahead_coefficient"
                  label="Day-Ahead Coefficient"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the day-ahead coefficient",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Coefficient"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="injection_absolute_day_ahead_coefficient"
                  label="Absolute Day-Ahead Coefficient"
                  tooltip={ABSOLUTE_COEFFICIENT_TOOLTIP}
                  initialValue={0}
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter the absolute day-ahead coefficient",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Coefficient"
                    addonAfter="× |Day-ahead price|"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="injection_cost_per_mwh"
                  label="Additional Cost"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the additional cost",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Additional Cost"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>
            <div className="mb-4 rounded bg-gray-200 p-3">
              <Text type="secondary">Price Formula (Injection Revenue)</Text>
              <div className="mt-1 font-mono text-gray-800">
                {formatDayAheadFormula(
                  injectionCoefficient,
                  injectionAbsoluteCoefficient,
                  injectionCost,
                )}
              </div>
            </div>
          </>
        )}

        {contractType === "day_night_be" && (
          <>
            <Divider orientation="left">Day/Night Contract (Belgium)</Divider>
            <Text strong>Offtake Cost</Text>
            <Row gutter={16} className="mt-2">
              <Col span={8}>
                <Form.Item
                  name="day_offtake_cost_per_mwh"
                  label="Day Price"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the day offtake price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Day Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="night_offtake_cost_per_mwh"
                  label="Night Price"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the night offtake price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Night Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Text strong>Injection Revenue</Text>
            <Row gutter={16} className="mt-2">
              <Col span={8}>
                <Form.Item
                  name="day_injection_cost_per_mwh"
                  label="Day Price"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the day injection price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Day Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="night_injection_cost_per_mwh"
                  label="Night Price"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the night injection price",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Night Price"
                    addonAfter="€/MWh"
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <DisableForViewer>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateContract.isPending}
            className="mt-4"
            disabled={contractType == null}
          >
            Update Contract
          </Button>
        </DisableForViewer>

        {updateContract.isError && (
          <Alert
            message="Error"
            description="Something went wrong while updating the energy contract."
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
    </div>
  );
};

export default EnergyContractForm;
